import BaseMappingsModel from './baseMappingsModel';
import { convertResponseToAddress } from '@/types/mapbox';
import { DEFAULT_ADDRESS_RADIUS } from '@/constants/settings';

const MAPPINGS = {
  carrying_capacity: 'carrying_capacity',
  loading_address: 'loading_address',
  loading_address_radius: 'loading_address_radius',
  unloading_address: 'unloading_address',
  unloading_address_radius: 'unloading_address_radius',
  additional_loading_addresses: 'additional_loading_addresses',
  additional_unloading_addresses: 'additional_unloading_addresses',
  excluded_loading_countries: 'excluded_loading_countries',
  excluded_unloading_countries: 'excluded_unloading_countries',
  available_from: 'available_from',
  available_to: 'available_to',
  dont_show_without_dimensions: 'dont_show_without_dimensions',
  height: 'height',
  length: 'length',
  partial_load: 'partial_load',
  payment_rate_type_id: 'payment_rate_type_id',
  transport_bodytype_ids: 'transport_bodytype_ids',
  transports_loading_types: 'transports_loading_types',
  volume: 'volume',
  width: 'width',
  with_documents: 'with_documents',
  without_prepayment: 'without_prepayment',

  template_title: 'template_title',
};

class ExchangeTransportSearch extends BaseMappingsModel {
  constructor(data) {
    super(MAPPINGS, data);
  }

  static initialValues() {
    const values = {};

    for (let key in MAPPINGS) {
      if (['loading_address', 'unloading_address'].includes(key)) {
        values[key] = null;
      } else if ([
        'transport_bodytype_ids',
        'additional_loading_addresses',
        'additional_unloading_addresses',
        'excluded_loading_countries',
        'excluded_unloading_countries',
      ].includes(key)) {
        values[key] = [];
      } else if (['loading_address_radius', 'unloading_address_radius'].includes(key)) {
        values[key] = DEFAULT_ADDRESS_RADIUS;
      } else {
        values[key] = '';
      }
    }

    return values;
  }

  static clearData(data) {
    const values = {};

    for (let key in MAPPINGS) {
      if (data[key] !== '' && data[key] !== null && data[key] !== undefined) {
        values[key] = data[key];
      }
    }

    return values;
  }

  // Convert API template data to form values
  static formData(data) {
    const values = this.clearData(data);

    // Helper to normalize address for form
    const normalizeAddress = (addr) => {
      const lat = addr.latitude ?? addr.location?.latitude;
      const lng = addr.longitude ?? addr.location?.longitude;
      return {
        country_code: addr.country_code || '',
        full_address: addr.full_address || '',
        location: {
          latitude: lat !== undefined && lat !== null ? Number(lat) : 0,
          longitude: lng !== undefined && lng !== null ? Number(lng) : 0,
        },
        country: addr.country || '',
        state_province: addr.state_province || '',
        city: addr.city || '',
        street: addr.street || '',
        post_code: addr.post_code || '',
      };
    };

    // Handle loading address - from API template (plural) or existing form data (singular)
    if (data.loading_addresses && Array.isArray(data.loading_addresses) && data.loading_addresses.length > 0) {
      const addr = data.loading_addresses[0];
      values.loading_address = {
        value: normalizeAddress(addr),
        label: addr.full_address || '',
      };
      // Additional addresses (all except first)
      if (data.loading_addresses.length > 1) {
        values.additional_loading_addresses = data.loading_addresses.slice(1).map((a, index) => ({
          id: Date.now() + index,
          address: {
            value: normalizeAddress(a),
            label: a.full_address || '',
          },
        }));
      }
    } else if (data.loading_address) {
      values.loading_address = data.loading_address;
    }

    // Handle unloading address - from API template (plural) or existing form data (singular)
    if (data.unloading_addresses && Array.isArray(data.unloading_addresses) && data.unloading_addresses.length > 0) {
      const addr = data.unloading_addresses[0];
      values.unloading_address = {
        value: normalizeAddress(addr),
        label: addr.full_address || '',
      };
      // Additional addresses (all except first)
      if (data.unloading_addresses.length > 1) {
        values.additional_unloading_addresses = data.unloading_addresses.slice(1).map((a, index) => ({
          id: Date.now() + index + 1000,
          address: {
            value: normalizeAddress(a),
            label: a.full_address || '',
          },
        }));
      }
    } else if (data.unloading_address) {
      values.unloading_address = data.unloading_address;
    }

    // Handle dates - convert ISO strings to yyyy-MM-dd format for form
    if (data.available_from) {
      const date = new Date(data.available_from);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      values.available_from = `${year}-${month}-${day}`;
    }
    if (data.available_to) {
      const date = new Date(data.available_to);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      values.available_to = `${year}-${month}-${day}`;
    }

    // Handle checkbox fields - ensure boolean values
    if (data.partial_load !== undefined) values.partial_load = Boolean(data.partial_load);
    if (data.dont_show_without_dimensions !== undefined) values.dont_show_without_dimensions = Boolean(data.dont_show_without_dimensions);
    if (data.without_prepayment !== undefined) values.without_prepayment = Boolean(data.without_prepayment);
    if (data.with_documents !== undefined) values.with_documents = Boolean(data.with_documents);
    if (data.is_semi_trailer !== undefined) values.is_semi_trailer = Boolean(data.is_semi_trailer);
    if (data.is_truck !== undefined) values.is_truck = Boolean(data.is_truck);
    if (data.is_coupling !== undefined) values.is_coupling = Boolean(data.is_coupling);

    // Handle radius
    if (data.loading_address_radius) values.loading_address_radius = data.loading_address_radius;
    if (data.unloading_address_radius) values.unloading_address_radius = data.unloading_address_radius;

    // Handle loading types from template
    // Backend returns transports_loading_types directly as array of UUIDs
    if (data.transports_loading_types && data.transports_loading_types.length > 0) {
      values.transports_loading_types = data.transports_loading_types;
    } else if (data.transport_template_loading_types && data.transport_template_loading_types.length > 0) {
      values.transports_loading_types = data.transport_template_loading_types.map((item) =>
        typeof item === 'string' ? item : item.id
      );
    }

    // Handle body types from template
    // Backend returns transport_bodytypes_id as array of UUIDs
    if (data.transport_bodytypes_id && data.transport_bodytypes_id.length > 0) {
      values.transport_bodytype_ids = data.transport_bodytypes_id;
    } else if (data.transport_bodytype_ids) {
      values.transport_bodytype_ids = Array.isArray(data.transport_bodytype_ids)
        ? data.transport_bodytype_ids
        : [data.transport_bodytype_ids];
    } else if (data.transport_template_bodytypes && data.transport_template_bodytypes.length > 0) {
      // Fallback for raw template data
      values.transport_bodytype_ids = data.transport_template_bodytypes.map((item) =>
        typeof item === 'string' ? item : item.id
      );
    }

    // Handle payment rate type
    if (data.payment_rate_type_id) {
      values.payment_rate_type_id = data.payment_rate_type_id;
    }

    // Handle excluded countries from template (stored as excluded_loading_addresses)
    if (data.excluded_loading_addresses && Array.isArray(data.excluded_loading_addresses)) {
      values.excluded_loading_countries = data.excluded_loading_addresses.map((addr) => ({
        value: {
          country_code: addr.country_code || '',
          country: addr.country || '',
          full_address: addr.full_address || addr.country || '',
          location: addr.location || {
            longitude: Number(addr.longitude) || 0,
            latitude: Number(addr.latitude) || 0,
          },
        },
        label: addr.country || addr.full_address || '',
      }));
    }
    if (data.excluded_unloading_addresses && Array.isArray(data.excluded_unloading_addresses)) {
      values.excluded_unloading_countries = data.excluded_unloading_addresses.map((addr) => ({
        value: {
          country_code: addr.country_code || '',
          country: addr.country || '',
          full_address: addr.full_address || addr.country || '',
          location: addr.location || {
            longitude: Number(addr.longitude) || 0,
            latitude: Number(addr.latitude) || 0,
          },
        },
        label: addr.country || addr.full_address || '',
      }));
    }

    if (data.notification_sound_enabled !== undefined) {
      values.notification_sound_enabled = data.notification_sound_enabled;
    }

    if (data.notifications_enabled !== undefined) {
      values.notifications_enabled = data.notifications_enabled;
    }

    delete values.template_title;

    return values;
  }

  // Prepare data for search API
  static searchData(data) {
    const values = {};

    // Helper to safely convert to number (returns undefined if NaN)
    const safeNumber = (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    };

    // Helper to normalize address for search API
    const normalizeAddressForSearch = (addr) => {
      const result = {
        location: addr.location,
      };
      if (addr.country_code) result.country_code = addr.country_code;
      if (addr.full_address) result.full_address = addr.full_address;
      if (addr.country) result.country = addr.country;
      if (addr.state_province) result.state_province = addr.state_province;
      if (addr.city) result.city = addr.city;
      if (addr.street) result.street = addr.street;
      if (addr.post_code) result.post_code = addr.post_code;
      return result;
    };

    // Handle loading addresses - from form (singular) or template (plural array)
    if (data.loading_addresses && Array.isArray(data.loading_addresses)) {
      values.loading_addresses = data.loading_addresses.map((addr) => {
        const converted = convertResponseToAddress(addr);
        return normalizeAddressForSearch(converted);
      });
    } else if (data.loading_address) {
      const addr = 'value' in data.loading_address ? data.loading_address.value : convertResponseToAddress(data.loading_address);
      values.loading_addresses = [normalizeAddressForSearch(addr)];
    }

    // Add additional loading addresses
    if (data.additional_loading_addresses && data.additional_loading_addresses.length > 0) {
      const additionalAddrs = data.additional_loading_addresses
        .filter((item) => item.address?.value)
        .map((item) => normalizeAddressForSearch(item.address.value));
      if (!values.loading_addresses) {
        values.loading_addresses = [];
      }
      values.loading_addresses = [...values.loading_addresses, ...additionalAddrs];
    }

    // Handle unloading addresses - from form (singular) or template (plural array)
    if (data.unloading_addresses && Array.isArray(data.unloading_addresses)) {
      values.unloading_addresses = data.unloading_addresses.map((addr) => {
        const converted = convertResponseToAddress(addr);
        return normalizeAddressForSearch(converted);
      });
    } else if (data.unloading_address) {
      const addr = 'value' in data.unloading_address ? data.unloading_address.value : convertResponseToAddress(data.unloading_address);
      values.unloading_addresses = [normalizeAddressForSearch(addr)];
    }

    // Add additional unloading addresses
    if (data.additional_unloading_addresses && data.additional_unloading_addresses.length > 0) {
      const additionalAddrs = data.additional_unloading_addresses
        .filter((item) => item.address?.value)
        .map((item) => normalizeAddressForSearch(item.address.value));
      if (!values.unloading_addresses) {
        values.unloading_addresses = [];
      }
      values.unloading_addresses = [...values.unloading_addresses, ...additionalAddrs];
    }

    // Add radius
    const loadingRadius = safeNumber(data.loading_address_radius);
    if (loadingRadius !== undefined) {
      values.loading_address_radius = loadingRadius;
    }
    const unloadingRadius = safeNumber(data.unloading_address_radius);
    if (unloadingRadius !== undefined) {
      values.unloading_address_radius = unloadingRadius;
    }

    // Add dates
    if (data.available_from) {
      const date = new Date(data.available_from);
      date.setHours(0, 0, 0, 0);
      values.available_from = date.toISOString();
    }
    if (data.available_to) {
      const date = new Date(data.available_to);
      date.setHours(23, 59, 59, 0);
      values.available_to = date.toISOString();
    }

    // Add carrying capacity
    const carryingCapacity = safeNumber(data.carrying_capacity);
    if (carryingCapacity !== undefined) {
      values.carrying_capacity = carryingCapacity;
    }

    // Add body types (backend expects transport_bodytype_ids as array)
    if (data.transport_bodytype_ids) {
      const bodyTypes = Array.isArray(data.transport_bodytype_ids)
        ? data.transport_bodytype_ids
        : [data.transport_bodytype_ids];
      if (bodyTypes.length > 0 && bodyTypes[0]) {
        values.transport_bodytype_ids = bodyTypes;
      }
    }

    // Add loading types
    if (data.transports_loading_types && data.transports_loading_types.length > 0) {
      values.transports_loading_types = data.transports_loading_types;
    }

    // Add payment rate type
    if (data.payment_rate_type_id) {
      values.payment_rate_type_id = data.payment_rate_type_id;
    }

    // Add checkboxes
    if (data.partial_load) values.partial_load = Boolean(data.partial_load);
    if (data.dont_show_without_dimensions) values.dont_show_without_dimensions = Boolean(data.dont_show_without_dimensions);
    if (data.without_prepayment) values.without_prepayment = Boolean(data.without_prepayment);
    if (data.with_documents) values.with_documents = Boolean(data.with_documents);

    // Add dimensions
    const width = safeNumber(data.width);
    const height = safeNumber(data.height);
    const length = safeNumber(data.length);
    const volume = safeNumber(data.volume);
    if (width !== undefined) values.width = width;
    if (height !== undefined) values.height = height;
    if (length !== undefined) values.length = length;
    if (volume !== undefined) values.volume = volume;

    // Handle excluded countries - convert to addresses format for backend
    if (data.excluded_loading_countries && data.excluded_loading_countries.length > 0) {
      values.excluded_loading_addresses = data.excluded_loading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }
    if (data.excluded_unloading_countries && data.excluded_unloading_countries.length > 0) {
      values.excluded_unloading_addresses = data.excluded_unloading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }

    // Handle exclusions from raw template data (already in correct format)
    if (data.excluded_loading_addresses && Array.isArray(data.excluded_loading_addresses) && !values.excluded_loading_addresses) {
      values.excluded_loading_addresses = data.excluded_loading_addresses;
    }
    if (data.excluded_unloading_addresses && Array.isArray(data.excluded_unloading_addresses) && !values.excluded_unloading_addresses) {
      values.excluded_unloading_addresses = data.excluded_unloading_addresses;
    }

    return values;
  }

  // Prepare data for creating/updating a template
  static templateData(data) {
    const loadingAddress = data.loading_address?.value || data.loading_address;
    const unloadingAddress = data.unloading_address?.value || data.unloading_address;

    // Generate template title from addresses
    const fromCity = loadingAddress?.city || loadingAddress?.country || '';
    const toCity = unloadingAddress?.city || unloadingAddress?.country || '';
    const templateTitle = fromCity && toCity ? `${fromCity} → ${toCity}` : fromCity || toCity || 'Новый маршрут';

    const templateData = {
      template_title: templateTitle,
    };

    // Helper to clean address object - remove empty string values
    const cleanAddress = (addr) => {
      if (!addr) return null;
      const cleaned = {};
      for (const [key, value] of Object.entries(addr)) {
        if (value !== '' && value !== null && value !== undefined) {
          if (key === 'location' && typeof value === 'object') {
            // Keep location object as-is if it has valid coordinates
            if (value.latitude !== undefined || value.longitude !== undefined) {
              cleaned[key] = value;
            }
          } else {
            cleaned[key] = value;
          }
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : null;
    };

    // Add addresses (main + additional)
    const cleanedLoading = cleanAddress(loadingAddress);
    const cleanedUnloading = cleanAddress(unloadingAddress);

    // Start with main address
    const loadingAddresses = cleanedLoading ? [cleanedLoading] : [];
    const unloadingAddresses = cleanedUnloading ? [cleanedUnloading] : [];

    // Add additional addresses
    if (data.additional_loading_addresses && data.additional_loading_addresses.length > 0) {
      const additionalAddrs = data.additional_loading_addresses
        .filter((item) => item.address?.value)
        .map((item) => cleanAddress(item.address.value))
        .filter(Boolean);
      loadingAddresses.push(...additionalAddrs);
    }
    if (data.additional_unloading_addresses && data.additional_unloading_addresses.length > 0) {
      const additionalAddrs = data.additional_unloading_addresses
        .filter((item) => item.address?.value)
        .map((item) => cleanAddress(item.address.value))
        .filter(Boolean);
      unloadingAddresses.push(...additionalAddrs);
    }

    if (loadingAddresses.length > 0) {
      templateData.loading_addresses = loadingAddresses;
    }
    if (unloadingAddresses.length > 0) {
      templateData.unloading_addresses = unloadingAddresses;
    }

    // Helper to safely convert to number (returns undefined if NaN)
    const safeNumber = (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    };

    // Add radius
    const loadingRadius = safeNumber(data.loading_address_radius);
    if (loadingRadius !== undefined) {
      templateData.loading_address_radius = loadingRadius;
    }
    const unloadingRadius = safeNumber(data.unloading_address_radius);
    if (unloadingRadius !== undefined) {
      templateData.unloading_address_radius = unloadingRadius;
    }

    // Add dates
    if (data.available_from) {
      const date = new Date(data.available_from);
      date.setHours(0, 0, 0, 0);
      templateData.available_from = date.toISOString();
    }
    if (data.available_to) {
      const date = new Date(data.available_to);
      date.setHours(23, 59, 59, 0);
      templateData.available_to = date.toISOString();
    }

    // Add carrying capacity
    const carryingCapacity = safeNumber(data.carrying_capacity);
    if (carryingCapacity !== undefined) {
      templateData.carrying_capacity = carryingCapacity;
    }

    // Add body types (backend expects transport_bodytype_ids as array)
    if (data.transport_bodytype_ids) {
      const bodyTypes = Array.isArray(data.transport_bodytype_ids)
        ? data.transport_bodytype_ids
        : [data.transport_bodytype_ids];
      if (bodyTypes.length > 0 && bodyTypes[0]) {
        templateData.transport_bodytype_ids = bodyTypes;
      }
    }

    // Add loading types
    if (data.transports_loading_types && data.transports_loading_types.length > 0) {
      templateData.transports_loading_types = data.transports_loading_types;
    }

    // Add payment rate type
    if (data.payment_rate_type_id) {
      templateData.payment_rate_type_id = data.payment_rate_type_id;
    }

    // Add checkboxes (these fields are required by database, default to false)
    templateData.partial_load = Boolean(data.partial_load);
    templateData.dont_show_without_dimensions = Boolean(data.dont_show_without_dimensions);
    templateData.without_prepayment = Boolean(data.without_prepayment);
    templateData.with_documents = Boolean(data.with_documents);

    // Add dimensions
    const width = safeNumber(data.width);
    const height = safeNumber(data.height);
    const length = safeNumber(data.length);
    const volume = safeNumber(data.volume);
    if (width !== undefined) templateData.width = width;
    if (height !== undefined) templateData.height = height;
    if (length !== undefined) templateData.length = length;
    if (volume !== undefined) templateData.volume = volume;

    // Add excluded addresses (countries)
    if (data.excluded_loading_countries && data.excluded_loading_countries.length > 0) {
      templateData.excluded_loading_addresses = data.excluded_loading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }
    if (data.excluded_unloading_countries && data.excluded_unloading_countries.length > 0) {
      templateData.excluded_unloading_addresses = data.excluded_unloading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }

    if (data.notification_sound_enabled !== undefined) {
      templateData.notification_sound_enabled = data.notification_sound_enabled;
    }

    if (data.notifications_enabled !== undefined) {
      templateData.notifications_enabled = data.notifications_enabled;
    }

    return templateData;
  }
}

export default ExchangeTransportSearch;
